import { Injectable, Signal, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Page } from '@website-builder/model/structure/page';
import { TbLink } from '@website-builder/model/domain/link/tb-link';
import { LinkType } from '@website-builder/model/domain/link/link-type.enum';
import { LinkTarget } from '@website-builder/model/domain/link/link-target.enum';
import { BaseWidget } from '@website-builder/widgets/base/base-widget';
import { Section } from '@website-builder/model/structure/section/section';
import { Row } from '@website-builder/model/structure/row/row';
import { BuilderLayoutPart } from '@website-builder/model/structure/component-part.type';
import { BuilderElementPosition } from '@website-builder/model/structure/builder-element-position';
import { BuilderMainPart } from '@website-builder/model/structure/builder-main-part.type';
import { NewPageResponse } from '@website-builder/model/structure/new-page-response';
import { BuilderElementEvent } from '@website-builder/application/model/events/builder-element-event.type';
import { BuilderDndEvent } from '@website-builder/application/model/events/builder-dnd-event.interface';
import { PageTemplateEvent } from '@website-builder/application/model/events/page-template-event.interface';
import { from, Observable, of, tap } from 'rxjs';
import { falseValueEqualityFn } from '@core/utilities/no-equal.utility';
import { BuilderRootStructureType } from '@website-builder/model/state/builder-root-structure.type';

@Injectable()
export class BuilderCommunicationService {
  document: Document | null = null;
  routingConfig: { route: ActivatedRoute; router: Router };
  internalNavigationHandler: ((path: string) => void) | null = null;

  readonly #toolboxState = signal<boolean>(null);
  readonly #toolboxWidgetScrollRequest = signal<string>(null, { equal: falseValueEqualityFn });

  readonly #activeWebsitePage = signal<Page>(null);
  readonly #pageStructureEvents = signal<NewPageResponse>(null);
  readonly #pageTemplateEvents = signal<PageTemplateEvent>(null);
  readonly #pageTemplateIdEvents = signal<BuilderMainPart>(null);
  readonly #widgetDndListener = signal<BuilderDndEvent | null>(null, { equal: falseValueEqualityFn });
  readonly #widgetEvents = signal<BuilderElementEvent | null>(null);
  readonly #rowEvents = signal<BuilderElementEvent | null>(null, { equal: falseValueEqualityFn });
  readonly #sectionEvents = signal<BuilderElementEvent | null>(null);

  readonly activeWebsitePage = this.#activeWebsitePage.asReadonly();
  readonly pageStructureEvents = this.#pageStructureEvents.asReadonly();
  readonly pageTemplateEvents = this.#pageTemplateEvents.asReadonly();
  readonly pageTemplateIdEvents = this.#pageTemplateIdEvents.asReadonly();
  readonly widgetEvents = this.#widgetEvents.asReadonly();
  readonly widgetDndListener = this.#widgetDndListener.asReadonly();
  readonly rowEvents = this.#rowEvents.asReadonly();
  readonly sectionEvents = this.#sectionEvents.asReadonly();
  readonly toolboxWidgetScrollRequest = this.#toolboxWidgetScrollRequest.asReadonly();

  readonly #builderElementChange = signal<BaseWidget | Section | Row>(null, { equal: falseValueEqualityFn });
  readonly #builderLayoutPartChange = signal<BuilderLayoutPart>(null);

  readonly builderElementChange = this.#builderElementChange.asReadonly();
  readonly builderLayoutPartChange = this.#builderLayoutPartChange.asReadonly();

  #tempScrollTopOffset: number | null = null;
  #scrollToTopAfterBodyUnlock = false;
  #scrollToTopAfterPageChange = false;

  toggleBodyScroll(disable: boolean, panel?: boolean): void {
    if (disable && this.#tempScrollTopOffset === null) {
      this.#tempScrollTopOffset = window.pageYOffset;
    }

    if (disable) {
      document.body.style.top = `-${this.#tempScrollTopOffset}px`;
      document.body.classList.add('block-scroll');
      if (panel) {
        document.documentElement.classList.add('panel-open');
      }
    } else {
      document.body.classList.remove('block-scroll');
      document.documentElement.classList.remove('panel-open');
      document.body.style.top = '';
      window.scrollTo({
        top: this.#scrollToTopAfterBodyUnlock ? 0 : (this.#tempScrollTopOffset ?? window.pageYOffset),
      });
      this.#tempScrollTopOffset = null;
      this.#scrollToTopAfterBodyUnlock = false;
    }
  }

  navigate(link: TbLink): void {
    if (!link?.url) {
      return;
    }

    const urlTrim = link.url.trim();

    if (link.type === LinkType.Website) {
      if (this.#openPageReference(link, urlTrim)) {
        return;
      }

      const target = link.target === LinkTarget.NewTab ? '_blank' : '_self';
      window.open(urlTrim, target);
      return;
    }

    const path = this.#resolveInternalPath(link.url);

    if (this.internalNavigationHandler) {
      this.internalNavigationHandler(path);
      return;
    }

    if (path.startsWith('/')) {
      void this.routingConfig.router.navigate(path.split('/'));
    } else {
      void this.routingConfig.router.navigate(path.split('/'), {
        relativeTo: this.routingConfig.route.parent,
      });
    }
  }

  navigate$(link: TbLink): Observable<boolean> {
    if (!link?.url) {
      return of(null);
    }

    const urlTrim = link.url.trim();

    if (link.type === LinkType.Website) {
      if (this.#openPageReference(link, urlTrim)) {
        return of(true);
      }

      const target = link.target === LinkTarget.NewTab ? '_blank' : '_self';
      window.open(urlTrim, target);
      return of(true);
    }

    const path = this.#resolveInternalPath(link.url);

    if (this.internalNavigationHandler) {
      this.#requestScrollToTopAfterPageChange();
      this.internalNavigationHandler(path);
      this.#scrollToTopAfterSuccessfulNavigation(true);
      return of(true);
    }

    if (path.startsWith('/')) {
      return from(this.routingConfig.router.navigate(path.split('/'))).pipe(
        tap(isNavigationSuccess => this.#scrollToTopAfterSuccessfulNavigation(isNavigationSuccess)),
      );
    } else {
      return from(
        this.routingConfig.router.navigate(path.split('/'), {
          relativeTo: this.routingConfig.route.parent,
        }),
      ).pipe(tap(isNavigationSuccess => this.#scrollToTopAfterSuccessfulNavigation(isNavigationSuccess)));
    }
  }

  notifyBuilderElementChange(value: BaseWidget | Section | Row): void {
    this.#builderElementChange.set(value);
  }

  builderLayoutChange(builderLayout: BuilderLayoutPart): void {
    this.#builderLayoutPartChange.set(builderLayout);
  }

  triggerToolboxState(visible: boolean): void {
    this.#toolboxState.set(visible);
  }

  requestToolboxWidgetScroll(widgetId: string): void {
    this.#toolboxWidgetScrollRequest.set(widgetId);
  }

  emitPageTemplateEvent(componentPosition: PageTemplateEvent): void {
    this.#pageTemplateEvents.set(componentPosition);
  }

  emitPageTemplateIdEvent(template: BuilderMainPart): void {
    return this.#pageTemplateIdEvents.set(template);
  }

  emitPageStructureEvent(page: NewPageResponse): void {
    return this.#pageStructureEvents.set(page);
  }

  get toolboxState(): Signal<boolean> {
    return this.#toolboxState.asReadonly();
  }

  onDndWidgetListener(type: BuilderRootStructureType, highlightedElement?: BuilderElementPosition): void {
    this.#widgetDndListener.set({
      rootBuilderElementType: type,
      highlightedElement,
    });
  }

  emitWidgetEvents(widgetEvents: BuilderElementEvent): void {
    this.#widgetEvents.set(widgetEvents);
  }

  emitRowEvent(rowEvent: BuilderElementEvent): void {
    this.#rowEvents.set(rowEvent);
  }

  emitSectionEvent(sectionEvent: BuilderElementEvent): void {
    this.#sectionEvents.set(sectionEvent);
  }

  changePage(page: Page): void {
    this.#activeWebsitePage.set(page);
    this.#scrollToTopAfterActivePageChange();
  }

  updatePage(key: string, newValue: string | number): void {
    this.#activeWebsitePage.update(page => {
      page[key] = newValue;

      return { ...page };
    });
  }

  #resolveInternalPath(url: string): string {
    const raw = url.trim();
    const match = raw.match(/^page:\s*(.+)$/i);

    return match ? match[1] : raw;
  }

  #openPageReference(link: TbLink, url: string): boolean {
    const urlMatch = url.match(/^page:\s*(.+)$/i);

    if (!urlMatch) {
      return false;
    }

    const websiteId = this.#getRouteParam('websiteId');

    if (!websiteId) {
      return false;
    }

    const pageLink = urlMatch[1].trim();
    const tree = this.routingConfig.router.createUrlTree(this.#getRuntimePageCommands(websiteId, pageLink));
    const target = link.target === LinkTarget.NewTab ? '_blank' : '_self';

    window.open(this.routingConfig.router.serializeUrl(tree), target);

    return true;
  }

  #getRuntimePageCommands(websiteId: string, pageLink: string): string[] {
    const commands = ['/website-builder', websiteId, 'view'];

    return pageLink && pageLink !== '/' ? [...commands, pageLink] : commands;
  }

  #getRouteParam(paramName: string): string | null {
    let route: ActivatedRoute | null = this.routingConfig.route;

    while (route) {
      const value = route.snapshot.paramMap.get(paramName);

      if (value) {
        return value;
      }

      route = route.parent;
    }

    return null;
  }

  #scrollToTopAfterSuccessfulNavigation(isNavigationSuccess: boolean): void {
    if (!isNavigationSuccess) {
      return;
    }

    this.#requestScrollToTopAfterPageChange();
    this.#scrollToTop();
  }

  #requestScrollToTopAfterPageChange(): void {
    this.#scrollToTopAfterPageChange = true;
  }

  #scrollToTopAfterActivePageChange(): void {
    if (!this.#scrollToTopAfterPageChange) {
      return;
    }

    this.#scrollToTopAfterPageChange = false;

    requestAnimationFrame(() => {
      this.#scrollToTop();
    });
  }

  #scrollToTop(): void {
    const scrollableContainer = document.getElementById('website_scrolling_container');
    scrollableContainer?.scrollTo({ top: 0, left: 0 });

    if (document.body.classList.contains('block-scroll')) {
      this.#scrollToTopAfterBodyUnlock = true;
      return;
    }

    window.scrollTo({ top: 0, left: 0 });
  }
}
